import { apiClient, assertValidParam } from '@v2/lib';
import type { Account, AccountDetails, AccountPayload, AccountWithPartnerDomains } from '@v2/types';

/**
 * Accounts Service
 *
 * Domain: User account management
 * Base path: /accounts
 *
 * Queries:
 *   - getAccounts()              → GET /accounts
 *   - getAccountDetails(id)      → GET /accounts/:id
 *   - getAccountsWithPartnerDomains(partnerId) → GET /accounts/withPartnerDomains?partnerId=
 *
 * Mutations:
 *   - createAccount(payload)     → POST /accounts
 *   - updateAccount(id, payload) → PATCH /accounts/:id
 *   - addAccountToPartner(partnerId, accountId) → POST /accounts/addToPartner?partnerId=
 *   - removeAccountFromPartner(partnerId, accountId) → DELETE /accounts/deleteFromPartner?partnerId=&accountId=
 */

export const accountsService = {
  getAccounts: async (): Promise<Account[]> => {
    const response = await apiClient().get<Account[]>('accounts');
    return response.data;
  },

  getAccountDetails: async (id: string): Promise<AccountDetails> => {
    const safeId = assertValidParam(id, 'accountId');
    const response = await apiClient().get<AccountDetails>(`accounts/${safeId}`);
    return response.data;
  },

  getAccountsWithPartnerDomains: async (
    partnerId: string
  ): Promise<AccountWithPartnerDomains[]> => {
    const safeId = assertValidParam(partnerId, 'partnerId');
    const response = await apiClient().get<AccountWithPartnerDomains[]>(
      'accounts/withPartnerDomains',
      { params: { partnerId: safeId } }
    );
    return response.data;
  },

  createAccount: async (payload: AccountPayload): Promise<string> => {
    const response = await apiClient().post<string>('accounts', payload);
    return response.data;
  },

  updateAccount: async (
    id: string,
    payload: Partial<AccountDetails>
  ): Promise<void> => {
    const safeId = assertValidParam(id, 'accountId');
    await apiClient().patch(`accounts/${safeId}`, payload);
  },

  addAccountToPartner: async (
    partnerId: string,
    accountId: string
  ): Promise<string> => {
    const safePartnerId = assertValidParam(partnerId, 'partnerId');
    const safeAccountId = assertValidParam(accountId, 'accountId');
    const response = await apiClient().post<string>(
      'accounts/addToPartner',
      { id: safeAccountId },
      { params: { partnerId: safePartnerId } }
    );
    return response.data;
  },

  removeAccountFromPartner: async (
    partnerId: string,
    accountId: string
  ): Promise<void> => {
    const safePartnerId = assertValidParam(partnerId, 'partnerId');
    const safeAccountId = assertValidParam(accountId, 'accountId');
    await apiClient().delete('accounts/deleteFromPartner', {
      params: { partnerId: safePartnerId, accountId: safeAccountId },
    });
  },
};
